import React from 'react';
import {
	type collectAll,
	type collectAny,
	ExperienceStart,
	ExperienceSuccess,
	useExperienceFail,
} from '@atlassian/jira-experience-tracker';
import { JIRA_SOFTWARE } from '@atlassian/jira-shared-types';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller';

export const VIEW_CODE_PAGE_EXPERIENCE_NAME = 'viewCodePage' as const;
export const VIEW_CODE_MAIN_PAGE_EXPERIENCE_NAME = 'viewCodeMainPage' as const;
export const VIEW_REPO_LIST_EXPERIENCE_NAME = 'viewCodePageRepoList' as const;
export const VIEW_PR_LIST_EXPERIENCE_NAME = 'viewCodePagePrList' as const;
export const VIEW_CONNECTION_PAGE_EXPERIENCE_NAME = 'viewCodePageConnectionPage' as const;

type CodePageExperiences =
	| typeof VIEW_CODE_PAGE_EXPERIENCE_NAME
	| typeof VIEW_CODE_MAIN_PAGE_EXPERIENCE_NAME
	| typeof VIEW_REPO_LIST_EXPERIENCE_NAME
	| typeof VIEW_PR_LIST_EXPERIENCE_NAME
	| typeof VIEW_CONNECTION_PAGE_EXPERIENCE_NAME;

type CodePageExperienceStartProps = {
	experienceId: string | null;
	experienceName?: CodePageExperiences;
	collect?: ReturnType<typeof collectAll> | ReturnType<typeof collectAny>;
};

export const CodePageExperienceStart = ({
	experienceId,
	experienceName = VIEW_CODE_PAGE_EXPERIENCE_NAME,
	collect,
}: CodePageExperienceStartProps) => {
	const appEditions = useAppEditions();
	return (
		<ExperienceStart
			experienceId={experienceId}
			experience={experienceName}
			analyticsSource="code"
			edition={appEditions.software}
			application={JIRA_SOFTWARE}
			collect={collect || undefined}
		/>
	);
};

type Attributes = {
	readonly [key: string]: string | number | boolean | null;
};

type OnExperienceFail = (location: string, error: Error, additionalAttributes?: Attributes) => void;

type UseViewCodePageExperienceFailArgs = {
	experienceName?: CodePageExperiences;
};

/**
 * The function returned by this hook should be called if an "unexpected error"
 * occurs, e.g. a JavaScript exception bubbles out of the Code Page.
 */
export const useViewCodePageExperienceFail = ({
	experienceName = VIEW_CODE_PAGE_EXPERIENCE_NAME,
}: UseViewCodePageExperienceFailArgs): OnExperienceFail =>
	useExperienceFail({
		experience: experienceName,
	});

/**
 * This component should be rendered in "expected error" scenarios, e.g. the
 * user does not have the correct permissions or application license to view
 * the Code Page.
 */
type ExpectedErrorSuccessProps = {
	reason: string;
};

export const ViewCodePageExpectedErrorExperienceSuccessTracker = ({
	reason,
}: ExpectedErrorSuccessProps) => (
	<ExperienceSuccess
		experience={VIEW_CODE_PAGE_EXPERIENCE_NAME}
		attributes={{
			reason,
		}}
	/>
);

type ViewCodePageExperienceSuccessTrackerProps = {
	experienceName?: CodePageExperiences;
};

/**
 * This component should be rendered when a Code Page experience succeeds.
 * Takes an optional `experienceName` prop to indicate which experience was successful.
 * Once rendered, this will fire a 'ui taskSuccess' GasV3 event if there's a pending start event with matching experience name.
 */
export const ViewCodePageExperienceSuccessTracker = ({
	experienceName = VIEW_CODE_PAGE_EXPERIENCE_NAME,
}: ViewCodePageExperienceSuccessTrackerProps) => <ExperienceSuccess experience={experienceName} />;
